import React, { useCallback, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { makeStyles } from '@material-ui/core/styles'
import SydButton from '../../../../../../commonDesign/Button'
import GroupEditor from './GroupEditor'
import FieldEditor from './FieldEditor'
import { DraggableGroupRow, DraggableFieldRow } from './DraggableSidebarItems'

function getSortedFieldEntries (group) {
  const entries = Object.entries(group.fields || {})
  return entries
    .map(([id, f], insertionIndex) => [id, f, insertionIndex])
    .sort((a, b) => (a[1].ordinal ?? a[2]) - (b[1].ordinal ?? b[2]))
    .map(([id, f]) => [id, f])
}

const useStyles = makeStyles(() => ({
  root: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--color-surface-000)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 16px',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)'
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: 'var(--font-size-sm)',
    color: 'var(--text-color-primary)'
  },
  body: {
    display: 'flex',
    minHeight: '420px',
    maxHeight: '600px'
  },
  sidebar: {
    width: '220px',
    minWidth: '220px',
    borderRight: '1px solid var(--color-neutral-200)',
    overflowY: 'auto',
    backgroundColor: 'var(--color-neutral-050)'
  },
  searchBox: {
    padding: '6px 10px',
    borderBottom: '1px solid var(--color-neutral-200)'
  },
  searchInput: {
    width: '100%',
    padding: '5px 8px',
    fontSize: '11px',
    border: '1px solid var(--color-neutral-200)',
    borderRadius: '4px',
    backgroundColor: 'var(--color-surface-000)',
    outline: 'none',
    boxSizing: 'border-box',
    '&:focus': {
      borderColor: 'var(--color-primary-400)'
    },
    '&::placeholder': {
      color: 'var(--color-neutral-300)'
    }
  },
  sidebarEmpty: {
    color: 'var(--text-color-subtle)',
    fontSize: 'var(--font-size-xs)',
    padding: '40px 20px',
    textAlign: 'center',
    lineHeight: 1.6
  },
  groupBlock: {
    '&:not(:last-child)': {
      borderBottom: '1px solid var(--color-neutral-150)'
    }
  },
  groupRow: {
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: 'var(--text-color-primary)',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)'
    }
  },
  groupRowSelected: {
    backgroundColor: 'var(--color-primary-050)',
    color: 'var(--color-primary-700)',
    '&:hover': {
      backgroundColor: 'var(--color-primary-050)'
    }
  },
  groupChevron: {
    fontSize: '8px',
    color: 'var(--text-color-subtle)',
    flexShrink: 0,
    transition: 'transform 0.15s',
    width: '10px',
    textAlign: 'center'
  },
  groupChevronOpen: {
    transform: 'rotate(90deg)'
  },
  groupLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0
  },
  groupFieldCount: {
    fontSize: '10px',
    color: 'var(--text-color-subtle)',
    backgroundColor: 'var(--color-neutral-200)',
    borderRadius: '8px',
    padding: '0 5px',
    lineHeight: '16px',
    flexShrink: 0
  },
  fieldList: {
    paddingBottom: '4px'
  },
  fieldRow: {
    padding: '4px 12px 4px 24px',
    cursor: 'pointer',
    fontSize: '11px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: 'var(--text-color-secondary)',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)',
      color: 'var(--text-color-primary)'
    }
  },
  fieldRowSelected: {
    backgroundColor: 'var(--color-primary-100)',
    color: 'var(--color-primary-700)',
    '&:hover': {
      backgroundColor: 'var(--color-primary-100)'
    }
  },
  fieldLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0
  },
  fieldTypeBadge: {
    color: 'var(--text-color-subtle)',
    flexShrink: 0,
    fontFamily: 'monospace',
    fontSize: '9px',
    letterSpacing: '0.3px'
  },
  addFieldRow: {
    padding: '3px 12px 3px 24px',
    fontSize: '11px',
    color: 'var(--color-primary-500)',
    cursor: 'pointer',
    '&:hover': {
      color: 'var(--color-primary-700)',
      textDecoration: 'underline'
    }
  },
  panel: {
    flex: 1,
    padding: '20px 24px',
    overflowY: 'auto'
  },
  panelEmpty: {
    color: 'var(--text-color-subtle)',
    fontSize: 'var(--font-size-sm)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '8px'
  },
  panelEmptyHint: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-neutral-400)'
  }
}))

function FieldMappingBuilder ({ fieldMapping, onChange }) {
  const classes = useStyles()
  const groups = useMemo(() => fieldMapping?.groups || [], [fieldMapping?.groups])
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(null)
  const [selectedFieldId, setSelectedFieldId] = useState(null)
  const [collapsedGroups, setCollapsedGroups] = useState(new Set())
  const [sidebarSearch, setSidebarSearch] = useState('')

  const updateGroups = useCallback((newGroups) => {
    onChange({ ...fieldMapping, groups: newGroups })
  }, [fieldMapping, onChange])

  const handleAddGroup = useCallback(() => {
    const newGroup = {
      id: `group_${Date.now()}`,
      title: 'New Group',
      description: '',
      ordinal: groups.length,
      fields: {}
    }
    updateGroups([...groups, newGroup])
    setSelectedGroupIndex(groups.length)
    setSelectedFieldId(null)
  }, [groups, updateGroups])

  const handleRemoveGroup = useCallback((index) => {
    const newGroups = groups.filter((_, i) => i !== index)
    updateGroups(newGroups)
    if (selectedGroupIndex === index) {
      setSelectedGroupIndex(null)
      setSelectedFieldId(null)
    } else if (selectedGroupIndex > index) {
      setSelectedGroupIndex(selectedGroupIndex - 1)
    }
  }, [groups, updateGroups, selectedGroupIndex])

  const handleUpdateGroup = useCallback((index, updatedGroup) => {
    const newGroups = [...groups]
    newGroups[index] = updatedGroup
    updateGroups(newGroups)
  }, [groups, updateGroups])

  const handleMoveGroup = useCallback((index, direction) => {
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= groups.length) return
    const newGroups = [...groups]
    const temp = newGroups[index]
    newGroups[index] = newGroups[newIndex]
    newGroups[newIndex] = temp
    updateGroups(newGroups.map((g, i) => ({ ...g, ordinal: i })))
    setSelectedGroupIndex(newIndex)
  }, [groups, updateGroups])

  const handleAddField = useCallback((groupIndex) => {
    const group = groups[groupIndex]
    const fields = group.fields || {}
    const fieldId = `field_${Date.now()}`
    const fieldCount = Object.keys(fields).length
    const newField = {
      id: fieldId,
      editorType: 'text',
      label: 'New Field',
      description: '',
      ordinal: fieldCount,
      required: false,
      defaultValue: '',
      initFrom: '',
      validationRules: {},
      display: { size: { xs: 12 } }
    }
    const updatedGroup = {
      ...group,
      fields: { ...fields, [fieldId]: newField }
    }
    handleUpdateGroup(groupIndex, updatedGroup)
    setSelectedGroupIndex(groupIndex)
    setSelectedFieldId(fieldId)
  }, [groups, handleUpdateGroup])

  const handleUpdateField = useCallback((groupIndex, fieldId, updatedField) => {
    const group = groups[groupIndex]
    const fields = { ...group.fields }

    if (updatedField.id !== fieldId) {
      if (fields[updatedField.id]) return
      delete fields[fieldId]
      fields[updatedField.id] = updatedField
    } else {
      fields[fieldId] = updatedField
    }

    handleUpdateGroup(groupIndex, { ...group, fields })
    if (updatedField.id !== fieldId) {
      setSelectedFieldId(updatedField.id)
    }
  }, [groups, handleUpdateGroup])

  const handleRemoveField = useCallback((groupIndex, fieldId) => {
    const group = groups[groupIndex]
    const fields = { ...group.fields }
    delete fields[fieldId]
    handleUpdateGroup(groupIndex, { ...group, fields })
    if (selectedFieldId === fieldId) {
      setSelectedFieldId(null)
    }
  }, [groups, handleUpdateGroup, selectedFieldId])

  const handleDuplicateField = useCallback((groupIndex, fieldId) => {
    const group = groups[groupIndex]
    const sourceField = group.fields?.[fieldId]
    if (!sourceField) return

    const newId = `${fieldId}_copy_${Date.now()}`
    const entries = getSortedFieldEntries(group)
    const sourceIndex = entries.findIndex(([id]) => id === fieldId)
    const newField = { ...sourceField, id: newId, label: `${sourceField.label || fieldId} (copy)`, ordinal: sourceIndex + 1 }

    const newFields = {}
    entries.forEach(([id, f], i) => {
      newFields[id] = { ...f, ordinal: i >= sourceIndex + 1 ? i + 1 : i }
    })
    newFields[newId] = newField

    handleUpdateGroup(groupIndex, { ...group, fields: newFields })
    setSelectedFieldId(newId)
  }, [groups, handleUpdateGroup])

  const handleDragMoveGroup = useCallback((fromIndex, toIndex) => {
    const newGroups = [...groups]
    const [moved] = newGroups.splice(fromIndex, 1)
    newGroups.splice(toIndex, 0, moved)
    updateGroups(newGroups.map((g, i) => ({ ...g, ordinal: i })))
    if (selectedGroupIndex === fromIndex) {
      setSelectedGroupIndex(toIndex)
    } else if (selectedGroupIndex !== null) {
      if (fromIndex < selectedGroupIndex && toIndex >= selectedGroupIndex) {
        setSelectedGroupIndex(selectedGroupIndex - 1)
      } else if (fromIndex > selectedGroupIndex && toIndex <= selectedGroupIndex) {
        setSelectedGroupIndex(selectedGroupIndex + 1)
      }
    }
  }, [groups, updateGroups, selectedGroupIndex])

  const handleDragMoveField = useCallback((groupIndex, fromFieldIndex, toFieldIndex) => {
    const group = groups[groupIndex]
    const sorted = getSortedFieldEntries(group)
    const [moved] = sorted.splice(fromFieldIndex, 1)
    sorted.splice(toFieldIndex, 0, moved)

    const newFields = {}
    sorted.forEach(([id, field], i) => {
      newFields[id] = { ...field, ordinal: i }
    })
    handleUpdateGroup(groupIndex, { ...group, fields: newFields })
  }, [groups, handleUpdateGroup])

  const handleMoveField = useCallback((groupIndex, fieldId, direction) => {
    const group = groups[groupIndex]
    const fieldEntries = getSortedFieldEntries(group)
    const currentIndex = fieldEntries.findIndex(([id]) => id === fieldId)
    const targetIndex = currentIndex + direction
    if (targetIndex < 0 || targetIndex >= fieldEntries.length) return

    const reordered = [...fieldEntries]
    const temp = reordered[currentIndex]
    reordered[currentIndex] = reordered[targetIndex]
    reordered[targetIndex] = temp

    const newFields = {}
    reordered.forEach(([id, field], i) => {
      newFields[id] = { ...field, ordinal: i }
    })
    handleUpdateGroup(groupIndex, { ...group, fields: newFields })
  }, [groups, handleUpdateGroup])

  const selectedGroup = selectedGroupIndex !== null ? groups[selectedGroupIndex] : null
  const selectedField = selectedGroup && selectedFieldId
    ? selectedGroup.fields?.[selectedFieldId] || null
    : null

  const selectedGroupFieldEntries = useMemo(() => {
    return selectedGroup ? getSortedFieldEntries(selectedGroup) : []
  }, [selectedGroup])

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <span className={classes.headerTitle}>Form Fields</span>
        <SydButton variant='ghost' size='xs' icon='add' onClick={handleAddGroup}>Add Group</SydButton>
      </div>

      <DndProvider backend={HTML5Backend}>
        <div className={classes.body}>
          <div className={classes.sidebar}>
            {groups.length > 0 && (
              <div className={classes.searchBox}>
                <input
                  className={classes.searchInput}
                  placeholder='Search fields...'
                  value={sidebarSearch}
                  onChange={(e) => setSidebarSearch(e.target.value)}
                />
              </div>
            )}
            {groups.length === 0 ? (
              <div className={classes.sidebarEmpty}>
                No field groups yet.<br />Add a group to start building the form.
              </div>
            ) : (
              groups.map((group, index) => {
                const isGroupSelected = selectedGroupIndex === index && !selectedFieldId
                const allEntries = getSortedFieldEntries(group)
                const searchLower = sidebarSearch.toLowerCase().trim()
                const entries = searchLower
                  ? allEntries.filter(([fId, field]) =>
                    (field.label || '').toLowerCase().includes(searchLower) ||
                    fId.toLowerCase().includes(searchLower) ||
                    (field.editorType || '').toLowerCase().includes(searchLower)
                  )
                  : allEntries
                const fieldCount = allEntries.length
                const groupKey = group.id || `idx_${index}`
                const isSearching = searchLower.length > 0
                if (isSearching && entries.length === 0) return null
                const isCollapsed = !isSearching && collapsedGroups.has(groupKey)

                const toggleCollapse = (e) => {
                  e.stopPropagation()
                  setCollapsedGroups(prev => {
                    const next = new Set(prev)
                    if (next.has(groupKey)) next.delete(groupKey)
                    else next.add(groupKey)
                    return next
                  })
                }

                return (
                  <DraggableGroupRow
                    key={groupKey}
                    groupIndex={index}
                    onMoveGroup={handleDragMoveGroup}
                    className={classes.groupBlock}
                  >
                    <div
                      className={`${classes.groupRow} ${isGroupSelected ? classes.groupRowSelected : ''}`}
                      onClick={() => { setSelectedGroupIndex(index); setSelectedFieldId(null) }}
                      role='button'
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setSelectedGroupIndex(index); setSelectedFieldId(null) } }}
                    >
                      <span
                        className={`${classes.groupChevron} ${isCollapsed ? '' : classes.groupChevronOpen}`}
                        onClick={toggleCollapse}
                        role='button'
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') toggleCollapse(e) }}
                      >
                        &#9654;
                      </span>
                      <span className={classes.groupLabel}>{group.title || 'Untitled'}</span>
                      <span className={classes.groupFieldCount}>{fieldCount}</span>
                    </div>

                    {!isCollapsed && (
                      <div className={classes.fieldList}>
                        {entries.map(([fId, field], fieldIndex) => {
                          const isFieldSelected = selectedFieldId === fId && selectedGroupIndex === index
                          return (
                            <DraggableFieldRow
                              key={fId}
                              groupIndex={index}
                              fieldIndex={isSearching ? -1 : fieldIndex}
                              fieldId={fId}
                              onMoveField={handleDragMoveField}
                              className={`${classes.fieldRow} ${isFieldSelected ? classes.fieldRowSelected : ''}`}
                              onClick={(e) => { e.stopPropagation(); setSelectedGroupIndex(index); setSelectedFieldId(fId) }}
                            >
                              <span className={classes.fieldLabel}>{field.label || fId}</span>
                              <span className={classes.fieldTypeBadge}>{field.editorType}</span>
                            </DraggableFieldRow>
                          )
                        })}
                        <div
                          className={classes.addFieldRow}
                          role='button'
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); handleAddField(index) }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleAddField(index) } }}
                        >
                          + add field
                        </div>
                      </div>
                    )}
                  </DraggableGroupRow>
                )
              })
            )}
          </div>

          <div className={classes.panel}>
            {selectedGroupIndex !== null && selectedGroup && !selectedFieldId && (
              <GroupEditor
                group={selectedGroup}
                groupIndex={selectedGroupIndex}
                totalGroups={groups.length}
                onChange={(updated) => handleUpdateGroup(selectedGroupIndex, updated)}
                onDelete={() => handleRemoveGroup(selectedGroupIndex)}
                onMove={(dir) => handleMoveGroup(selectedGroupIndex, dir)}
              />
            )}
            {selectedGroupIndex !== null && selectedField && (
              <FieldEditor
                field={selectedField}
                fieldEntries={selectedGroupFieldEntries}
                onChange={(updated) => handleUpdateField(selectedGroupIndex, selectedFieldId, updated)}
                onDelete={() => handleRemoveField(selectedGroupIndex, selectedFieldId)}
                onMove={(dir) => handleMoveField(selectedGroupIndex, selectedFieldId, dir)}
                onDuplicate={() => handleDuplicateField(selectedGroupIndex, selectedFieldId)}
              />
            )}
            {selectedGroupIndex === null && (
              <div className={classes.panelEmpty}>
                <span>Select a group or field to edit its properties</span>
                <span className={classes.panelEmptyHint}>
                  {groups.length === 0 ? 'Start by adding a group above' : 'Click an item in the sidebar'}
                </span>
              </div>
            )}
          </div>
        </div>
      </DndProvider>
    </div>
  )
}

FieldMappingBuilder.propTypes = {
  fieldMapping: PropTypes.shape({
    groups: PropTypes.array
  }),
  onChange: PropTypes.func.isRequired
}

export default FieldMappingBuilder
